import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { SessionStorageService } from '@app/shared/services/storage/session-storage.service';
import { BlockTypeEnum } from '@core/enums';
import { NeighbourModelsInfo, Page } from '@core/interfaces/page.interface';
import {
  LIST_CONTEXT_STORAGE_KEY,
  parseListContextStore,
  pickListContextEntry,
} from '@modules/main/helpers/list-context.helper';
import { NEIGHBOUR_LIST_BLOCK_TYPE_BY_PAGE_TYPE } from '@modules/main/helpers/neighbour-list-source.helper';
import { NeighbourListNavigationService } from '@modules/main/services/neighbour-list-navigation.service';

import { Observable, of } from 'rxjs';
import { mergeMap, tap } from 'rxjs/operators';
import { ApiBlockService } from '../api/api-block.service';
import { ApiUrlService } from '../common/api-url.service';
import { CurrencyPreferenceService } from '@shared/services/currency/currency-preference.service';

@Injectable({providedIn: 'root'})
export class ApiPageService {
  private apiUrl: string = this.apiUrlService.getApiUrl() + 'client/pages/';
  private apiUrlNeighbourModels: string = this.apiUrlService.getApiUrl() + 'client/page-neighbour-models/';

  constructor(private httpClient: HttpClient,
              private apiUrlService: ApiUrlService,
              private apiBlockService: ApiBlockService,
              private currencyPreferenceService: CurrencyPreferenceService,
              private storageService: SessionStorageService,
              private neighbourListSourceService: NeighbourListNavigationService,
              @Inject(DOCUMENT) private document: Document,
              @Inject(PLATFORM_ID) private platformId: any) {
  }

  getBySlug(slug: string, noCache = false, editMode = false): Observable<Page> {
    let headers: any = {
      'x-show-loader': '1',
      'key': slug ? '' : this.apiBlockService.parseLocalizationBySystem(),
    };
    const urlParams = [];

    let url = this.apiUrl + slug;
    if (noCache) {
      headers = {
        ...headers,
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache',
      };

      urlParams.push('no-cache');
    }
    if (editMode) {
      headers = {
        ...headers,
        'edit-mode': '1',
      };
      urlParams.push('edit-mode');
    }
    if (urlParams.length) {
      url = url + '?' + urlParams.join('&');
    }
    url = this.currencyPreferenceService.appendCurrencyToUrlIfSwitcherExists(url);
    // Dla SSR pobieramy tylko metadane i nic więcej
    return this.httpClient.get<Page>(url, {headers})
      .pipe(
        tap(page => this.currencyPreferenceService.setPageShouldConvertCurrency(page?.shouldConvertCurrency)),
        mergeMap(page => of(page)),
      );
  }

  getPage(slug?: string): Observable<Page> {
    if (!slug) {
      slug = this.document.location.pathname;
    }

    let noCache = false;
    let queryParamsString = '';

    if (slug.includes('?')) {
      [slug, queryParamsString] = slug.split('?');
      const urlParams = new URLSearchParams(queryParamsString);
      if (urlParams.has('no-cache')) {
        noCache = true;
      }
    }
    while (slug.charAt(0) === '/') {
      slug = slug.substring(1);
    }
    if (!slug && isPlatformBrowser(this.platformId)) {
      this.storageService.setItem('language', navigator.language.split('-')[0]);
    }

    let editMode = false;
    if (this.document.location.hash === '#edit' || slug.includes('#edit')) {
      noCache = true;
      editMode = true;
    }
    if (slug.includes('#')) {
      slug = slug.split('#')[0];
    }
    return this.getBySlug(slug, noCache, editMode);
  }

  getPageNeighbourModels(page: Page, slug: string): Observable<NeighbourModelsInfo> {
    const url = this.apiUrlNeighbourModels;
    const headers: any = {};

    if (isPlatformBrowser(this.platformId)) {
      const listTypeByModelPage = NEIGHBOUR_LIST_BLOCK_TYPE_BY_PAGE_TYPE[page.type];
      if (listTypeByModelPage) {
        /*
         * AT-9155: parametry bierzemy z listy, z ktorej odwiedzajacy faktycznie przyszedl —
         * zrodlo zna osobna pamiec dla nawigacji sasiadow (nie pamiec „Powrotu do listy",
         * ktora celowo nie przetrwalaby skokow podgladu-na-podglad).
         */
        const listQueryParams = this.getSourceListQueryParams(listTypeByModelPage);
        if (listQueryParams) {
          headers['list-query-params'] = listQueryParams;
        }
      }
    }
    return this.httpClient.get<NeighbourModelsInfo>(url, {headers, params: {slug}});
  }

  /**
   * Parametry listy bedacej zrodlem wejscia na biezacy podglad. Brak zapamietanego
   * zrodla (wejscie bezposrednie, odswiezenie, albo zrodlo bez kontekstu tej listy)
   * znaczy brak naglowka - backend uzywa wtedy domyslnych ustawien bloku listy.
   */
  private getSourceListQueryParams(blockType: BlockTypeEnum): string {
    const sourceUrl = this.neighbourListSourceService.getSourceUrl(this.document.location.pathname);
    if (!sourceUrl) {
      return '';
    }

    const store = parseListContextStore(this.storageService.getItem(LIST_CONTEXT_STORAGE_KEY));

    return pickListContextEntry(store, sourceUrl, blockType)?.listQueryParams || '';
  }
}
